// Shared scaffold for print artifacts (Outline, Learning Record, Transcript).
// Light theme is mandatory — these pages print on white paper.

import type { ReactNode } from 'react'
import { Printer } from 'lucide-react'

export const R = {
  ink: '#141B2E', slate: '#5B6472', muted: '#8A94A6',
  gold: '#A8842C', goldSoft: '#C9A85C', goldTint: '#FBF6EA',
  border: '#E5E1D8', cream: '#FAF8F3', white: '#FFFFFF',
  green: '#15803D', amber: '#B45309', rose: '#BE123C',
} as const

export const serif = "'Playfair Display', Georgia, serif"
export const sans = "'Inter', system-ui, -apple-system, sans-serif"

export const PRINT_CSS = `
@page { margin: 14mm; }
@media print {
  html, body, #root, div, main {
    overflow: visible !important;
    height: auto !important;
  }
  .no-print { display: none !important; }
  .record-page { box-shadow: none !important; margin: 0 !important; width: 100% !important; max-width: none !important; }
  .print-block { break-inside: avoid; }
  .module-header { break-after: avoid; }
}
`

export function PrintFrame({ children, maxWidth = 880 }: { children: ReactNode; maxWidth?: number }) {
  return (
    <div style={{ minHeight: '100dvh', background: '#ECE9E1', fontFamily: sans }}>
      <style>{PRINT_CSS}</style>
      <div className="record-page"
        style={{ maxWidth, margin: '0 auto', background: R.white, minHeight: '100dvh',
                 boxShadow: '0 0 44px rgba(20,27,46,0.10)', padding: '36px 44px 56px', color: R.ink }}>
        {children}
      </div>
    </div>
  )
}

export function PrintButton({ label = 'Print / Save PDF' }: { label?: string }) {
  return (
    <button className="no-print" onClick={() => window.print()}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: R.gold, color: '#fff',
               border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
      <Printer size={15} /> {label}
    </button>
  )
}

export function RecordHeader({ eyebrow, title, subtitle, right }: {
  eyebrow: string; title: string; subtitle?: string; right?: ReactNode
}) {
  return (
    <div className="module-header"
      style={{ borderBottom: `2px solid ${R.ink}`, paddingBottom: 14, marginBottom: 22,
               display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.18em', textTransform: 'uppercase', color: R.gold, marginBottom: 6 }}>
          {eyebrow}
        </div>
        <h1 style={{ fontFamily: serif, fontSize: 28, fontWeight: 800, margin: 0, color: R.ink, lineHeight: 1.15 }}>{title}</h1>
        {subtitle && <p style={{ margin: '6px 0 0', color: R.slate, fontSize: 14 }}>{subtitle}</p>}
      </div>
      {right}
    </div>
  )
}

export function StatRow({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <div className="print-block"
      style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 10, marginBottom: 24 }}>
      {items.map((s, i) => (
        <div key={i} style={{ background: R.cream, border: `1px solid ${R.border}`, borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: R.ink, fontVariantNumeric: 'tabular-nums' }}>{s.value}</div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: R.muted, marginTop: 3 }}>{s.label}</div>
        </div>
      ))}
    </div>
  )
}

export type RecordVariant = 'homeschool' | 'church' | 'university' | 'school' | 'business' | 'ngo' | 'community'

export function resolveRecordVariant(params: {
  searchContext?: string | null
  searchVariant?: string | null
  childOrgType?: string | null
  childFacilityType?: string | null
  userOrgType?: string | null
  userFacilityType?: string | null
  isFaithSubject?: boolean
}): RecordVariant {
  const explicit = (params.searchVariant || params.searchContext || '').toLowerCase()
  if (explicit === 'church' || explicit === 'business' || explicit === 'university' || explicit === 'school' || explicit === 'ngo' || explicit === 'community' || explicit === 'homeschool') {
    return explicit as RecordVariant
  }
  if (params.isFaithSubject) return 'church'

  const resolved = (params.childOrgType || params.childFacilityType || params.userOrgType || params.userFacilityType || '').toLowerCase()
  if (resolved === 'business' || resolved === 'corporate') return 'business'
  if (resolved === 'university' || resolved === 'college' || resolved === 'higher_ed') return 'university'
  if (resolved === 'school' || resolved === 'k12') return 'school'
  if (resolved === 'church' || resolved === 'ministry' || resolved === 'bible_college') return 'church'
  if (resolved === 'ngo' || resolved === 'university_ngo') return 'ngo'
  if (resolved === 'community' || resolved === 'sports_club') return 'community'
  return 'homeschool'
}

export function getRecordCopy(variant: RecordVariant, orgName?: string | null) {
  const brandName = orgName || 'Still Waters Academy'
  switch (variant) {
    case 'business':
      return {
        eyebrow: `${brandName} · Professional Development`,
        transcriptSubtitle: 'Organisation record of study',
        learningRecordEyebrow: `${brandName} · Training Record`,
        feedbackLabel: 'Supervisor & Assessor Feedback',
        supervisorTitle: 'Training Manager / Organisation Supervisor',
        footer: `${brandName} · Competency-Verified Training · Mastery threshold 80%`,
        notesPrompt: 'Full per-module records with quiz evidence and practical tasks are available as a Learning Record for each course.',
        standardTitle: 'The Professional Competency Standard',
        standardBody: 'Every module publishes its verified learning scope: workplace competencies, practical skills, and core industry concepts. Every lesson is assessed at an 80% mastery threshold; assessments are generated directly from taught concepts and verified server-side. Practical tasks and written submissions are retained with rubric and supervisor feedback.',
        standardTagline: 'Competency-verified · Mastery-based · Evidence-backed training record',
      }
    case 'university':
      return {
        eyebrow: `${brandName} · Academic Transcript`,
        transcriptSubtitle: 'Academic record of study',
        learningRecordEyebrow: `${brandName} · Academic Record`,
        feedbackLabel: 'Faculty & Instructor Feedback',
        supervisorTitle: 'Registrar / Academic Dean',
        footer: `${brandName} · Academic Curriculum of Record · Mastery threshold 80%`,
        notesPrompt: 'Full per-lesson academic records with examination evidence and paper submissions are available as a Learning Record for each course.',
        standardTitle: 'The Academic Curriculum Standard',
        standardBody: 'Every course publishes its complete academic syllabus: modules, lecture units, and the core theoretical concepts taught in each. Every lesson is assessed at an 80% mastery threshold; examinations and written papers are scored server-side and retained with evaluator feedback. The syllabus of record represents verified academic study.',
        standardTagline: 'Documented · Mastery-based · Evidence-backed academic record',
      }
    case 'school':
      return {
        eyebrow: `${brandName} · Academic Transcript`,
        transcriptSubtitle: 'School record of study',
        learningRecordEyebrow: `${brandName} · Academic Record`,
        feedbackLabel: 'Teacher Feedback',
        supervisorTitle: 'Class Teacher / Principal',
        footer: `${brandName} · Mastery-Based Curriculum · Mastery threshold 80%`,
        notesPrompt: 'Full per-lesson records with quiz evidence, retakes, and written work are available as a Learning Record for each course.',
        standardTitle: 'The School Curriculum Standard',
        standardBody: 'Every course publishes its complete scope before enrolment: modules, lessons, and the concepts taught in each. Every lesson is assessed at an 80% mastery threshold; quizzes are generated from the lesson\'s taught concepts and scored server-side. Every assessment persists its questions, score, and date.',
        standardTagline: 'Documented · Mastery-based · Evidence-backed curriculum',
      }
    case 'church':
      return {
        eyebrow: `${brandName} · Bible College`,
        transcriptSubtitle: 'Bible College record of study',
        learningRecordEyebrow: `${brandName} · Bible College`,
        feedbackLabel: 'Teacher & Ministry Feedback',
        supervisorTitle: 'Pastor / Discipleship Leader / Ministry Mentor',
        footer: `${brandName} · Bible College · Scripture-Grounded Christian Education · stillwatersacademy.com`,
        notesPrompt: 'Full per-lesson Scripture records with quiz evidence and reflection work are available as a Learning Record for each course.',
        standardTitle: 'The Bible College Standard',
        standardBody: 'A record of study in the Bible College, Scripture-grounded, mastery-based Christian education. Every course publishes its complete Biblical scope: modules, passages studied, and core theological concepts taught. Every lesson is assessed at an 80% mastery threshold; assessments and written work are verified and retained with rubric and mentor feedback. This document represents an evidence-backed record of journey through Scripture.',
        standardTagline: 'Scripture-grounded · Mastery-based · Discipleship record — stillwatersacademy.com',
      }
    case 'ngo':
      return {
        eyebrow: `${brandName} · Programme Record`,
        transcriptSubtitle: 'Programme record of study',
        learningRecordEyebrow: `${brandName} · Programme Learning Record`,
        feedbackLabel: 'Programme Coordinator & Mentor Feedback',
        supervisorTitle: 'Programme Coordinator',
        footer: `${brandName} · Programme Curriculum · Mastery threshold 80%`,
        notesPrompt: 'Full per-unit programme records with practical assessments and mentor feedback are available as a Learning Record for each course.',
        standardTitle: 'The Programme Curriculum Standard',
        standardBody: 'Every programme publishes its complete learning scope: modules, community sessions, and practical skills taught in each. Every unit is assessed at an 80% mastery threshold; participant progress, practical demonstrations, and facilitator feedback are verified and retained.',
        standardTagline: 'Documented · Mastery-based · Programme record of study',
      }
    case 'community':
      return {
        eyebrow: `${brandName} · Community Learning`,
        transcriptSubtitle: 'Community record of study',
        learningRecordEyebrow: `${brandName} · Community Record`,
        feedbackLabel: 'Facilitator Feedback',
        supervisorTitle: 'Community Coordinator / Facilitator',
        footer: `${brandName} · Community Learning · Mastery threshold 80%`,
        notesPrompt: 'Full per-module records with interactive evidence and workshop milestones are available as a Learning Record for each course.',
        standardTitle: 'The Community Learning Standard',
        standardBody: 'Every course publishes its complete learning scope: modules, workshops, and practical concepts taught in each. Every lesson is assessed at an 80% mastery threshold; workshop completion and facilitator feedback are verified and retained.',
        standardTagline: 'Documented · Mastery-based · Community record of study',
      }
    case 'homeschool':
    default:
      return {
        eyebrow: 'Still Waters Academy · Academic Transcript',
        transcriptSubtitle: 'Homeschool record of study',
        learningRecordEyebrow: 'Still Waters Academy · Master Curriculum',
        feedbackLabel: 'Feedback & Encouragement',
        supervisorTitle: 'Parent / Guardian — Teacher of Record',
        footer: 'Still Waters Academy · Master Curriculum · Lesson mastery threshold 80% · stillwatersacademy.com',
        notesPrompt: 'Full per-lesson records with quiz evidence, retakes, and written work are available as a Learning Record for each course (Parent Dashboard → subject → Print Record).',
        standardTitle: 'The Still Waters Curriculum Standard',
        standardBody: 'Every course publishes its complete scope before enrolment: modules, lessons, and the concepts taught in each. Every lesson is assessed at an 80% mastery threshold; quizzes are generated from the lesson\'s taught concepts and scored server-side. Every assessment persists its questions, score, and date. Written work is retained with rubric and feedback. The module plan is the curriculum of record: the concepts listed are the concepts taught.',
        standardTagline: 'Documented · Mastery-based · Evidence-backed — stillwatersacademy.com',
      }
  }
}

export function StandardStatement({ variant = 'homeschool', orgName }: { variant?: RecordVariant; orgName?: string | null } = {}) {
  const copy = getRecordCopy(variant, orgName)
  return (
    <div className="print-block" style={{ marginTop: 28, background: R.goldTint, border: '1px solid #E8D9AE', borderRadius: 12, padding: '16px 20px' }}>
      <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', color: R.gold, marginBottom: 8 }}>
        {copy.standardTitle}
      </div>
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.65, color: R.ink }}>
        {copy.standardBody}
      </p>
      <p style={{ margin: '8px 0 0', fontSize: 11, color: R.slate, fontStyle: 'italic' }}>
        {copy.standardTagline}
      </p>
    </div>
  )
}

export function SupervisorBlock({ variant = 'homeschool', orgName }: { variant?: RecordVariant; orgName?: string | null } = {}) {
  const copy = getRecordCopy(variant, orgName)
  return (
    <div className="print-block" style={{ marginTop: 36, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
      <div style={{ borderTop: `1.5px solid ${R.ink}`, paddingTop: 8, fontSize: 11, color: R.slate }}>
        {copy.supervisorTitle}
      </div>
      <div style={{ borderTop: `1.5px solid ${R.ink}`, paddingTop: 8, fontSize: 11, color: R.slate }}>Date</div>
    </div>
  )
}

export function scoreTone(s: number) {
  return s >= 80 ? R.green : s >= 60 ? R.amber : R.rose
}

export function fmtDate(d?: string | null) {
  return d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
}

// ── Period filtering (transcripts) ─────────────────────────────────────────────
export interface PeriodRange { label: string; from: Date | null; to: Date | null }

export const PERIOD_PRESETS = [
  { key: 'all', label: 'All Time' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'semester', label: 'This Semester' },
  { key: 'year', label: 'This Year' },
  { key: 'last30', label: 'Last 30 Days' },
  { key: 'last90', label: 'Last 90 Days' },
  { key: 'custom', label: 'Custom…' },
] as const

export function resolvePreset(preset: string, now = new Date()): PeriodRange {
  const d = new Date(now)
  switch (preset) {
    case 'week': {
      const from = new Date(d); from.setDate(d.getDate() - ((d.getDay() + 6) % 7)); from.setHours(0, 0, 0, 0)
      return { label: 'This Week', from, to: null }
    }
    case 'month':
      return { label: 'This Month', from: new Date(d.getFullYear(), d.getMonth(), 1), to: null }
    case 'quarter': {
      const q = Math.floor(d.getMonth() / 3)
      return { label: `Q${q + 1} ${d.getFullYear()}`, from: new Date(d.getFullYear(), q * 3, 1), to: null }
    }
    case 'semester': {
      const s1 = d.getMonth() < 6
      return { label: s1 ? `Semester 1 ${d.getFullYear()}` : `Semester 2 ${d.getFullYear()}`,
               from: new Date(d.getFullYear(), s1 ? 0 : 6, 1), to: null }
    }
    case 'year':
      return { label: `${d.getFullYear()}`, from: new Date(d.getFullYear(), 0, 1), to: null }
    case 'last30': {
      const from = new Date(d); from.setDate(d.getDate() - 30)
      return { label: 'Last 30 Days', from, to: null }
    }
    case 'last90': {
      const from = new Date(d); from.setDate(d.getDate() - 90)
      return { label: 'Last 90 Days', from, to: null }
    }
    default:
      return { label: 'All Time', from: null, to: null }
  }
}

export function inRange(iso: string | null | undefined, r: PeriodRange): boolean {
  if (!iso) return false                       // no timestamp → no evidence → excluded when a range is active
  if (!r.from && !r.to) return true
  const t = new Date(iso).getTime()
  if (r.from && t < r.from.getTime()) return false
  if (r.to && t > r.to.getTime()) return false
  return true
}

export function fmtRange(r: PeriodRange): string {
  if (!r.from && !r.to) return 'All Time'
  return `${r.from ? fmtDate(r.from.toISOString()) : 'Start'} – ${r.to ? fmtDate(r.to.toISOString()) : 'Present'}`
}

// Screen-only control bar — never prints. Composes with PrintButton naturally:
// whatever is filtered on screen is what prints.
export function TranscriptControls(props: {
  preset: string; setPreset: (p: string) => void
  customFrom: string; setCustomFrom: (v: string) => void
  customTo: string; setCustomTo: (v: string) => void
  courses: { classId: string; className: string }[]
  selected: Set<string>; setSelected: (s: Set<string>) => void
}) {
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, courses, selected, setSelected } = props
  const allSelected = selected.size === 0
  const toggle = (id: string) => {
    const next = new Set(selected)
    next.has(id) ? next.delete(id) : next.add(id)
    setSelected(next)                           // empty set = all
  }
  const inputStyle: any = {
    padding: '6px 10px', border: `1px solid ${R.border}`, borderRadius: 8, fontSize: 12.5,
    background: R.white, color: R.ink, fontFamily: sans,
  }
  return (
    <div className="no-print" style={{ background: R.cream, border: `1px solid ${R.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 18 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: R.muted, marginRight: 4 }}>Period</span>
        {PERIOD_PRESETS.map(p => (
          <button key={p.key} onClick={() => setPreset(p.key)}
            style={{ padding: '5px 12px', borderRadius: 999, border: `1px solid ${preset === p.key ? R.gold : R.border}`,
                     background: preset === p.key ? R.gold : R.white, color: preset === p.key ? '#fff' : R.ink,
                     fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            {p.label}
          </button>
        ))}
        {preset === 'custom' && (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginLeft: 6 }}>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={inputStyle} />
            <span style={{ color: R.muted }}>→</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={inputStyle} />
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <button onClick={() => setSelected(new Set())}
          style={{ ...inputStyle, cursor: 'pointer', fontWeight: 800, borderColor: allSelected ? R.gold : R.border }}>
          All Courses{allSelected ? ` (${courses.length})` : ''}
        </button>
        {courses.map(c => {
          const on = allSelected || selected.has(c.classId)
          return (
            <button key={c.classId} onClick={() => toggle(c.classId)}
              style={{ padding: '5px 10px', borderRadius: 8, border: `1px solid ${on ? R.gold : R.border}`,
                       background: on ? R.goldTint : R.white, color: on ? R.gold : R.slate,
                       fontSize: 11.5, fontWeight: 600, cursor: 'pointer', maxWidth: 220,
                       overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {on ? '✓ ' : ''}{c.className}
            </button>
          )
        })}
      </div>
    </div>
  )
}
// ── Human feedback display (teacher overrides + parent encouragement) ────────
export interface FeedbackRow {
  id: string; author_name?: string | null
  feedback_type: string; body: string | null
  original_score?: number | null; override_score?: number | null; override_reason?: string | null
  targets_lesson: boolean; lesson_unit?: number | null; lesson_number?: number | null
  visible_to_student: boolean; created_at: string
}

export function FeedbackBlock({ rows, headerLabel = 'Teacher & Parent Feedback' }: { rows: FeedbackRow[]; headerLabel?: string }) {
  if (!rows.length) return null
  const icon: Record<string, string> = {
    lesson_comment: '💬', module_comment: '💬', grade_override: '✏️',
    encouragement: '💛', improvement_note: '📈',
  }
  return (
    <div className="print-block" style={{ marginTop: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: R.muted, marginBottom: 6 }}>
        {headerLabel}
      </div>
      {rows.map(f => (
        <div key={f.id} style={{ border: `1px solid ${R.border}`, borderRadius: 10, padding: '8px 12px', marginBottom: 6, background: f.feedback_type === 'grade_override' ? R.goldTint : R.cream }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
            <span style={{ fontWeight: 800, color: R.ink }}>
              {icon[f.feedback_type] || '💬'} {f.author_name || 'Instructor'}
              {f.targets_lesson && f.lesson_unit ? ` · Lesson ${f.lesson_unit}.${f.lesson_number ?? ''}` : ''}
            </span>
            <span style={{ color: R.muted }}>{fmtDate(f.created_at)}</span>
          </div>
          {f.feedback_type === 'grade_override' && (
            <div style={{ fontSize: 12, marginBottom: 3 }}>
              <span style={{ color: R.muted, textDecoration: 'line-through' }}>{f.original_score}%</span>
              {' '}
              <span style={{ color: R.gold, fontWeight: 800 }}>→ {f.override_score}%</span>
              {f.override_reason && <span style={{ color: R.slate }}> — {f.override_reason}</span>}
            </div>
          )}
          {f.body && <div style={{ fontSize: 12, color: R.ink, lineHeight: 1.5 }}>{f.body}</div>}
        </div>
      ))}
    </div>
  )
}
